import { useCallback, useEffect, useState } from 'react'
import { Button, Chip, Table } from '@heroui/react'
import { api } from '../lib/api'
import { listState } from '../lib/listState'
import { shortSHA } from '../lib/screenApps'
import { explain } from '../lib/staffErrors'
import { formatMoment } from '../lib/staffText'
import type { ScreenApp } from '../lib/types'
import { FilePicker } from '../ui/FilePicker'
import { useStaffText } from '../ui/StaffText'
import { Confirm, useFlash, useSave } from './sectionKit'
import { Icon } from '../ui/icons'
import { EmptyState } from '../ui/parts'
import { IconTile, InnerPage, ListCard, ListRow, ListTable, NameCell, Notice, PageHead, RowArrow, useOpenRecord } from './settingsKit'
import '../styles/screenapps.css'

/**
 * Screen apps: an organisation's own kiosk or hall board, uploaded as a zip and
 * placed on a machine (admin/equipment/ScreenAppSection.tsx).
 *
 * Read with catalogue.manage, the permission the Machines page already needs —
 * whoever places an app must be able to see which apps there are. Uploading,
 * choosing a version and retiring need tenant.manage, because the code that runs
 * is the organisation's own and reaches every kiosk it is placed on (P10b plan,
 * ruling 9). `canManage` draws that line: a catalogue.manage holder without it
 * sees the same rows and drawers with none of the controls.
 */
export function ScreenApps({ organisation, canManage }: { organisation?: string; canManage: boolean }) {
  const { lang, say } = useStaffText()
  const { flash, toast } = useFlash()
  const [apps, setApps] = useState<ScreenApp[]>([])
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState('')
  const [uploading, setUploading] = useState(false)
  const [find, setFind] = useState('')

  const load = useCallback(async () => {
    try {
      const { apps: rows } = await api.screenApps(organisation)
      setApps(rows)
    } catch (err) {
      setError(explain(err, lang, 'common.unreachable'))
    } finally {
      setLoaded(true)
    }
    // lang deliberately not a dependency: a language switch must not reload.
  }, [organisation])

  useEffect(() => {
    void load()
  }, [load])

  const upload = async (file: File) => {
    setUploading(true)
    setError('')
    try {
      await api.uploadScreenApp(file, organisation)
      await load()
      flash(say('connect.appUploaded'))
    } catch (err) {
      setError(explain(err, lang, 'common.unreachable'))
    } finally {
      setUploading(false)
    }
  }

  // The app open on its own page (#screenApps/<id>).
  const rec = useOpenRecord(apps, (a) => a.id, loaded)
  const state = listState(loaded, apps.length)

  const q = find.trim().toLocaleLowerCase()
  const shown = apps.filter((a) => !q || `${a.name} ${a.key}`.toLocaleLowerCase().includes(q))

  if (rec.row) {
    return (
      <>
        <AppDrawer
          key={rec.row.id}
          app={rec.row}
          canManage={canManage}
          flash={flash}
          onClose={rec.close}
          onChanged={load}
          onRetired={async () => {
            await load()
            rec.close()
          }}
        />
        {toast}
      </>
    )
  }

  return (
    <>
      <PageHead
        title={say('admin.section.screenApps')}
        line={say('settings.apps.line')}
        actions={
          canManage ? (
            <span className="sapps-upload" title={say('connect.uploadRule')}>
              {uploading && <span className="sapps-muted">{say('screenApps.uploading')}</span>}
              <FilePicker accept=".zip" disabled={uploading} label={say('connect.uploadZip')} onFile={(f) => void upload(f)} />
            </span>
          ) : undefined
        }
      />
      {error && <Notice className="sapps-gap">{error}</Notice>}

      <ListCard
        title={say('settings.apps.list')}
        count={apps.length > 0 ? say('connect.appCount', { n: apps.length }) : undefined}
        search={find}
        onSearch={apps.length > 0 ? setFind : undefined}
        searchLabel={say('settings.apps.find')}
      >
        {state === 'loading' && <p className="empty">{say('common.loading')}</p>}
        {state === 'empty' && <EmptyState icon={<Icon name="machine" size={20} />} title={say('screenApps.none')} />}
        {apps.length > 0 && shown.length === 0 && <EmptyState title={say('admin.findNone', { q: find.trim() })} />}
        {shown.length > 0 && (
          <ListTable
            label={say('settings.apps.list')}
            cols="minmax(240px, 1.8fr) 120px 170px 16px"
            head={[say('settings.apps.col.app'), say('settings.apps.col.version'), say('settings.apps.col.machines'), '']}
          >
            {shown.map((app) => {
              const current = app.versions.find((v) => v.id === app.currentVersionId)
              return (
                <ListRow key={app.id} off={Boolean(app.retiredAt)} onOpen={() => rec.open(app)}>
                  <NameCell
                    tile={<IconTile icon={app.kind === 'kiosk' ? 'machine' : 'designs'} tone={app.kind === 'kiosk' ? 1 : 3} />}
                    name={
                      <>
                        {app.name}
                        <Chip size="sm" variant="secondary">
                          {say(app.kind === 'kiosk' ? 'common.role.kiosk' : 'common.role.display')}
                        </Chip>
                        {app.retiredAt && (
                          <Chip size="sm" variant="soft">
                            {say('common.retiredPill')}
                          </Chip>
                        )}
                      </>
                    }
                    sub={<span className="sapps-mono">{app.key}</span>}
                  />
                  <span role="cell" className="sapps-row__what">
                    <strong>{current?.version ?? '—'}</strong>
                    <span>{say('connect.versionCount', { n: app.versions.length })}</span>
                  </span>
                  <span role="cell" className="sapps-status" data-on={app.placedOn > 0 || undefined}>
                    <i aria-hidden="true" />
                    {app.placedOn > 0 ? say('connect.onMachines', { n: app.placedOn }) : say('connect.notPlaced')}
                  </span>
                  <RowArrow />
                </ListRow>
              )
            })}
          </ListTable>
        )}
      </ListCard>

      {toast}
    </>
  )
}

function AppDrawer({
  app,
  canManage,
  flash,
  onClose,
  onChanged,
  onRetired,
}: {
  app: ScreenApp
  canManage: boolean
  flash: (text: string) => void
  onClose: () => void
  onChanged: () => Promise<void>
  onRetired: () => Promise<void>
}) {
  const { lang, say } = useStaffText()
  const { busy, error, setError, save } = useSave(onChanged, flash)

  const preview = async () => {
    setError('')
    try {
      const { url } = await api.previewScreenApp(app.id, app.currentVersionId)
      // A new tab: a preview opened in this one would leave the administration
      // screen behind, and the sandboxed document has nothing to navigate back
      // with.
      window.open(url, '_blank', 'noopener')
    } catch (err) {
      setError(explain(err, lang, 'common.unreachable'))
    }
  }

  return (
    <InnerPage
      back={say('admin.section.screenApps')}
      title={app.name}
      onClose={onClose}
      wide
      extra={
        canManage && !app.retiredAt ? (
          app.placedOn > 0 ? (
            // Refused by the server while any machine runs it (APP_PLACED).
            <span>{say('connect.retireFirst', { n: app.placedOn })}</span>
          ) : (
            <Confirm
              ask={say('screenApps.retire')}
              question={say('connect.retireAsk')}
              yes={say('screenApps.retire')}
              keep={say('connect.keep')}
              busy={busy}
              onYes={() =>
                void save(() => api.retireScreenApp(app.id), say('connect.appRetired')).then((ok) => { if (ok) void onRetired() })
              }
            />
          )
        ) : undefined
      }
    >
      {error && <Notice>{error}</Notice>}

      <dl className="sapps-facts">
        <div>
          <dt>{say('connect.appId')}</dt>
          <dd className="sapps-mono">{app.key}</dd>
        </div>
        <div>
          <dt>{say('connect.kind')}</dt>
          <dd>{say(app.kind === 'kiosk' ? 'common.role.kiosk' : 'common.role.display')}</dd>
        </div>
        <div>
          <dt>{say('connect.machines')}</dt>
          <dd>{app.placedOn > 0 ? say('screenApps.placedOn', { n: app.placedOn }) : say('connect.notPlaced')}</dd>
        </div>
        {app.retiredAt && (
          <div>
            <dt>{say('common.retiredPill')}</dt>
            <dd>{formatMoment(app.retiredAt, lang, { date: true, time: true })}</dd>
          </div>
        )}
      </dl>

      {canManage && (
        <div className="sapps-line">
          <span>
            <strong title={say('connect.previewNote')}>{say('screenApps.preview')}</strong>
          </span>
          <Button variant="secondary" onPress={() => void preview()}>
            {say('connect.open')}
          </Button>
        </div>
      )}

      <fieldset className="field sapps-versions">
        <legend>{say('connect.versions')}</legend>
        <Table variant="secondary">
          <Table.ScrollContainer>
            <Table.Content aria-label={say('connect.versions')}>
              <Table.Header>
                <Table.Column isRowHeader>{say('screenApps.version')}</Table.Column>
                <Table.Column>{say('screenApps.uploadedAt')}</Table.Column>
                <Table.Column>{say('screenApps.sha')}</Table.Column>
                <Table.Column className="num">
                  <span className="sr-only">{say('screenApps.current')}</span>
                </Table.Column>
              </Table.Header>
              <Table.Body>
                {app.versions.map((v) => (
                  <Table.Row key={v.id} id={v.id}>
                    <Table.Cell className="sapps-version">{v.version}</Table.Cell>
                    <Table.Cell>{formatMoment(v.uploadedAt, lang, { date: true, time: true })}</Table.Cell>
                    <Table.Cell className="sapps-mono">
                      <span title={v.sha256}>{shortSHA(v.sha256)}</span>
                    </Table.Cell>
                    <Table.Cell className="num">
                      {v.id === app.currentVersionId ? (
                        <span className="sapps-status" data-on>
                          <i aria-hidden="true" />
                          {say('screenApps.current')}
                        </span>
                      ) : (
                        canManage && (
                          <Button
                            variant="secondary"
                            size="sm"
                            isDisabled={busy}
                            onPress={() =>
                              void save(
                                () => api.setCurrentScreenAppVersion(app.id, v.id),
                                say('connect.nowCurrent', { version: v.version }),
                              )
                            }
                          >
                            {say('screenApps.makeCurrent')}
                          </Button>
                        )
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Content>
          </Table.ScrollContainer>
        </Table>
      </fieldset>
    </InnerPage>
  )
}
